import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const STATUS_STYLES = {
  loading: { icon: null, color: '#6b7280', bg: '#1a2030' },
  valid: { icon: '✓', color: '#22c55e', bg: '#052e16' },
  revoked: { icon: '⚠', color: '#f59e0b', bg: '#422006' },
  not_found: { icon: '✗', color: '#ef4444', bg: '#450a0a' },
};

const STATUS_MESSAGES = {
  valid: { title: 'Document Verified', desc: 'This certificate confirms that the document is an authentic, certified penetration testing report.' },
  revoked: { title: 'Certificate Revoked', desc: 'This certificate has been revoked. The associated document should no longer be considered current.' },
  not_found: { title: 'Certificate Not Found', desc: 'This token does not match any certified document. Please ensure you are using the correct verification link.' },
};

export default function VerifyCertificate() {
  const { token } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/verify/${token}`);
        const data = await res.json();
        setResult(data);
      } catch {
        setResult({ status: 'not_found' });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const status = loading ? 'loading' : (result?.status || 'not_found');
  const style = STATUS_STYLES[status];
  const msg = STATUS_MESSAGES[status];

  const copyHash = () => {
    if (result?.content_hash) {
      navigator.clipboard?.writeText(result.content_hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#0f1219', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      padding: 20,
    }}>
      <div style={{ width: '100%', maxWidth: 480, textAlign: 'center' }}>
        {/* Logo */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span style={{ fontSize: 22, fontWeight: 700, color: '#e2e8f0', letterSpacing: '-0.02em' }}>AttackPath</span>
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: '#1a2030', borderRadius: 16, padding: 40,
          border: '1px solid #2d3748', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}>
          {loading ? (
            <div>
              <div style={{
                width: 64, height: 64, borderRadius: '50%', margin: '0 auto 20px',
                border: '3px solid #2d3748', borderTopColor: '#3b82f6',
                animation: 'spin 1s linear infinite',
              }} />
              <div style={{ fontSize: 16, color: '#94a3b8' }}>Verifying document...</div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : (
            <div>
              {/* Status icon */}
              <div style={{
                width: 72, height: 72, borderRadius: '50%', margin: '0 auto 20px',
                background: style.bg, border: `2px solid ${style.color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 36, color: style.color,
              }}>
                {style.icon}
              </div>

              <h1 style={{ fontSize: 22, fontWeight: 700, color: '#e2e8f0', margin: '0 0 8px' }}>
                {msg?.title}
              </h1>
              <p style={{ fontSize: 14, color: '#94a3b8', margin: '0 0 24px', lineHeight: 1.5 }}>
                {msg?.desc}
              </p>

              {status === 'valid' && (
                <div style={{ textAlign: 'left' }}>
                  <div style={{
                    background: '#0f1219', borderRadius: 8, padding: 16,
                    border: '1px solid #2d3748', marginBottom: 16,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                      <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Issued</span>
                      <span style={{ fontSize: 13, color: '#e2e8f0' }}>
                        {result.issued_at ? new Date(result.issued_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '-'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Certified by</span>
                      <span style={{ fontSize: 13, color: '#e2e8f0' }}>{result.organization || 'TSR Solutions'}</span>
                    </div>
                  </div>

                  {result.content_hash && (
                    <div style={{
                      background: '#0f1219', borderRadius: 8, padding: 16,
                      border: '1px solid #2d3748',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>SHA-256 Content Hash</span>
                        <button onClick={copyHash} style={{
                          background: 'none', border: '1px solid #2d3748', borderRadius: 4,
                          padding: '2px 8px', fontSize: 11, color: copied ? '#22c55e' : '#64748b',
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                          {copied ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                      <code style={{
                        display: 'block', fontSize: 11, color: '#94a3b8', fontFamily: 'monospace',
                        wordBreak: 'break-all', lineHeight: 1.6, background: '#111827',
                        padding: 10, borderRadius: 4,
                      }}>
                        {result.content_hash}
                      </code>
                      <p style={{ fontSize: 11, color: '#475569', margin: '10px 0 0', lineHeight: 1.4 }}>
                        To verify document integrity, compute the SHA-256 hash of the report file and compare it to the hash above.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ marginTop: 24, fontSize: 12, color: '#475569' }}>
          AttackPath by TSR Solutions
        </div>
      </div>
    </div>
  );
}
